import type { TimelineRow } from "@/services/conversations.api";

/**
 * Cache en memoria del timeline, por conversación.
 *
 * **Qué problema resuelve.** Cambiar de chat pedía siempre `getTimeline` y, mientras tanto, vaciaba
 * la lista y mostraba un skeleton. Volver a un chat que acabás de mirar costaba lo mismo que
 * abrirlo por primera vez.
 *
 * **Qué NO es.** No es un reemplazo del request: es sólo para pintar rápido. WhatsApp Web puede
 * saltar de chat sin pegarle a nadie porque replica la base entera en el cliente (IndexedDB +
 * protocolo multi-device); eso es otra arquitectura y para ~100 conversaciones es desproporcionado.
 *
 * Acá se usa *stale-while-revalidate*: se muestra lo cacheado y **siempre** se revalida. Hacerlo al
 * revés —"pedir sólo si no está en cache"— sería inseguro con lo que emitimos hoy:
 *
 *  - no hay evento de socket para un mensaje **editado** ni **borrado**, y esas columnas existen
 *    (`edited_at`, `deleted_at`);
 *  - el front sólo escucha la conversación **abierta** (`conversation:join`/`leave`), así que el
 *    cache de un chat cerrado no recibe nada mientras tanto.
 *
 * Vive en memoria del módulo: sobrevive a que el componente se desmonte, no a un reload. Es
 * deliberado — persistirlo traería un problema de invalidación que no queremos.
 */

/** Cuántas conversaciones se recuerdan. Con 50 filas cada una, el techo es despreciable. */
const MAX_CONVERSACIONES = 20;

const cache = new Map<string, TimelineRow[]>();

export function leerTimeline(conversationId: string): TimelineRow[] | undefined {
  const hit = cache.get(conversationId);
  if (!hit) return undefined;
  // Refresca la posición en el orden de inserción: `Map` lo mantiene, y es lo que usa el desalojo.
  cache.delete(conversationId);
  cache.set(conversationId, hit);
  return hit;
}

export function guardarTimeline(conversationId: string, rows: TimelineRow[]): void {
  if (!rows.length) return; // una lista vacía no aporta nada y pisaría un cache bueno
  cache.delete(conversationId);
  cache.set(conversationId, rows);
  while (cache.size > MAX_CONVERSACIONES) {
    const masViejo = cache.keys().next().value as string | undefined;
    if (masViejo === undefined) break;
    cache.delete(masViejo);
  }
}

export function olvidarTimeline(conversationId: string): void {
  cache.delete(conversationId);
}

/** Sólo para tests. */
export function vaciarTimelineCache(): void {
  cache.clear();
}

/**
 * Junta lo cacheado con la página fresca que devolvió el server.
 *
 * `getTimeline` trae la **ventana más reciente** (50 por default). El cache puede tener además lo
 * que el operador trajo con "cargar más". Reemplazar a secas perdería ese scroll-back y el chat
 * daría un salto visible a los 200 ms de abrirlo.
 *
 * Por construcción, lo que está en cache y NO está en la ventana fresca es **más viejo** que ella,
 * así que va como prefijo y no hace falta reordenar nada. Y lo que sí está en la ventana lo gana la
 * versión fresca — con eso una edición dentro de la ventana se corrige sola.
 *
 * **No alcanza con "no está en la ventana fresca" para darlo por viejo.** Una nota que otro operador
 * borró mientras el chat estaba cerrado también desaparece de la ventana —`note:deleted` sólo llega
 * a quien tiene esa conversación joineada— y tratarla como anterior la resucitaba **al tope del
 * chat**, fuera de su lugar cronológico. Por eso se compara contra el borde de la ventana: lo que
 * cae dentro de su rango y no vino, no está más.
 *
 * Limitación conocida: un mensaje borrado que quedó **antes** del borde de la ventana sigue
 * viéndose hasta recargar. Se cierra el día que haya un evento de borrado que llegue a todos.
 */
export function reconciliarTimeline(
  cacheado: TimelineRow[] | undefined,
  fresco: TimelineRow[],
): TimelineRow[] {
  if (!cacheado?.length) return fresco;
  if (!fresco.length) return cacheado;
  const enVentana = new Set(fresco.map((r) => r.id));
  // Borde de la ventana fresca: lo más viejo que el server acaba de confirmar.
  const borde = fresco.reduce(
    (min, r) => (r.occurredAt < min ? r.occurredAt : min),
    fresco[0].occurredAt,
  );
    // `<=` y no `<`: una fila cacheada empatada en timestamp con el borde ya quedó excluida por el
  // `enVentana` si vino en la ventana, así que acá sólo caen filas distintas — y ante la duda es
  // mejor conservar historia que tirarla. Lo borrado que importa cae DENTRO del rango, no en el borde.
  const anteriores = cacheado.filter((r) => !enVentana.has(r.id) && r.occurredAt <= borde);
  return [...anteriores, ...fresco];
}
